// @flow
import {
    CLEAR_TIMEOUT,
    TIMEOUT_TICK,
    SET_TIMEOUT,
    timerWorkerScript
} from './TimerWorker';
import WebGLBackgroundRenderer from './WebGLBackgroundRenderer';

/**
 * Represents a modified MediaStream that adds effects to video background.
 * <tt>KinoBackgroundEffect</tt> does the processing of the original video stream.
 *
 * The pipeline:
 *   - segmentation (TFLite/WASM) is run on a throttled cadence ({@code inferenceFrameRate}) rather
 *     than every frame, and the resulting person-probability mask is temporally smoothed (EMA) so
 *     it can be reused/blended between inference runs without flicker;
 *   - compositing is driven by {@code requestVideoFrameCallback} (falling back to the timer worker
 *     when unavailable or when the tab is hidden) at {@code renderFrameRate};
 *   - the masked blend + background blur is done on the GPU via {@link WebGLBackgroundRenderer},
 *     falling back to 2D-canvas + CSS-filter compositing when WebGL is unavailable.
 */
export default class KinoBackgroundEffect {
    /**
     * Represents a modified video MediaStream track.
     *
     * @class
     * @param {Object} model - Meet model.
     * @param {Object} options - Segmentation dimensions and per-device processing budget.
     */
    constructor(model, options) {
        this._model = model;
        this._options = options;
        this._segmentationPixelCount = this._options.width * this._options.height;
        this._segmentationType = this._options.segmentationType || 'single';

        // Render / inference cadence (see PERFORMANCE_BUDGET in index.js).
        this._renderFrameRate = this._options.renderFrameRate || this.getFrameRate();
        this._inferenceFrameRate = Math.min(
            this._options.inferenceFrameRate || this._renderFrameRate,
            this._renderFrameRate
        );
        this._renderIntervalMs = 1000 / this._renderFrameRate;
        this._maskSmoothing = typeof this._options.maskSmoothing === 'number' ? this._options.maskSmoothing : 0.6;
        this._blurIterations = this._options.blurIterations || 2;

        // Adaptive inference throttling. The configured tier rate is the *ceiling*; if segmentation
        // turns out too expensive on this device (measured at runtime) we run it less often so the
        // output frame rate is preserved at the cost of a slightly staler mask. We never run it more
        // often than the tier ceiling, and never slower than ~2 fps.
        this._tierInferenceIntervalMs = 1000 / this._inferenceFrameRate;
        this._maxInferenceIntervalMs = 500;
        this._inferenceIntervalMs = this._tierInferenceIntervalMs;
        this._inferDurationEma = 0;
        this._inferDutyTarget = 0.3;

        // Loop / driver state.
        this._stopped = false;
        this._loopRunning = false;
        this._activeDriver = null;
        this._rvfcId = 0;
        this._lastRenderTime = 0;
        this._lastInferenceTime = 0;
        this._hasMask = false;
        this._tickInProgress = false;

        // Bind handlers so they are only bound once per instance.
        this._onMaskFrameTimer = this._onMaskFrameTimer.bind(this);
        this._onVideoFrame = this._onVideoFrame.bind(this);
        this._onVisibilityChange = this._onVisibilityChange.bind(this);

        this._maskFrameTimerWorker = null;
        this._renderer = null;
        this._useWebGL = false;

        // Temporal smoothing buffer (EMA of person probability per pixel).
        this._smoothedMask = new Float32Array(this._segmentationPixelCount);

        if (this._options.virtualBackground.backgroundType === 'image') {
            this._virtualImage = document.createElement('img');
            this._virtualImage.crossOrigin = 'anonymous';
            this._virtualImage.onload = () => {
                if (this._renderer) {
                    this._renderer.setBackgroundImage(this._virtualImage);
                }
            };
            this._virtualImage.src = this._options.virtualBackground.virtualSource;
        }

        // Offscreen canvas used to downscale the camera frame to the model input resolution and to
        // read back pixels for the segmentation tensor.
        this._segmentationMask = new ImageData(this._options.width, this._options.height);
        this._segmentationMaskCanvas = document.createElement('canvas');
        this._segmentationMaskCanvas.width = this._options.width;
        this._segmentationMaskCanvas.height = this._options.height;
        this._segmentationMaskCtx = this._segmentationMaskCanvas.getContext('2d', { willReadFrequently: true });

        // 2D fallback output canvas (used only when WebGL is unavailable).
        this._outputCanvasElement = document.createElement('canvas');
        this._outputCanvasCtx = null;

        this._inputVideoElement = document.createElement('video');
        this._rvfcSupported = typeof this._inputVideoElement.requestVideoFrameCallback === 'function';
    }

    /**
     * Checks if the local track supports this effect.
     *
     * @param {JitsiLocalTrack} jitsiLocalTrack - Track to apply effect.
     * @returns {boolean} - Returns true if this effect can run on the specified track, false otherwise.
     */
    isEnabled(jitsiLocalTrack) {
        return jitsiLocalTrack.isVideoTrack() && jitsiLocalTrack.videoType === 'camera';
    }

    /**
     * Starts the capture / render loop and returns the processed stream.
     *
     * @param {MediaStream} stream - Stream to be used for processing.
     * @returns {MediaStream} - The stream with the applied effect.
     */
    startEffect(stream) {
        this._stopped = false;
        this._maskFrameTimerWorker = new Worker(timerWorkerScript, { name: 'Blur effect worker' });
        this._maskFrameTimerWorker.onmessage = this._onMaskFrameTimer;

        const firstVideoTrack = stream.getVideoTracks()[0];
        const { height, width }
            = firstVideoTrack.getSettings ? firstVideoTrack.getSettings() : firstVideoTrack.getConstraints();
        const frameWidth = parseInt(width, 10);
        const frameHeight = parseInt(height, 10);

        // Prefer the GPU renderer; fall back to 2D canvas compositing if WebGL is unavailable.
        try {
            this._renderer = new WebGLBackgroundRenderer(frameWidth, frameHeight, {
                blurIterations: this._blurIterations
            });
            this._useWebGL = true;

            if (this._virtualImage && this._virtualImage.complete && this._virtualImage.naturalWidth) {
                this._renderer.setBackgroundImage(this._virtualImage);
            }
        } catch (error) {
            console.warn('WebGL background renderer unavailable, using 2D canvas fallback', error);
            this._renderer = null;
            this._useWebGL = false;
        }

        if (!this._useWebGL) {
            this._outputCanvasElement.width = frameWidth;
            this._outputCanvasElement.height = frameHeight;
            this._outputCanvasCtx = this._outputCanvasElement.getContext('2d', { willReadFrequently: true });
        }

        this._inputVideoElement.width = frameWidth;
        this._inputVideoElement.height = frameHeight;
        this._inputVideoElement.autoplay = true;
        this._inputVideoElement.muted = true;
        this._inputVideoElement.playsInline = true;
        this._inputVideoElement.srcObject = stream;
        this._inputVideoElement.onloadeddata = () => {
            // autoplay is not always enough (e.g. Safari); ensure the element is playing so that
            // requestVideoFrameCallback fires.
            this._inputVideoElement.play().catch(() => { /* ignored */ });
            this._lastRenderTime = 0;
            this._lastInferenceTime = 0;

            // Guard against onloadeddata firing more than once, which would start parallel loops.
            if (!this._loopRunning) {
                this._loopRunning = true;
                this._scheduleNext();
            }
        };

        document.addEventListener('visibilitychange', this._onVisibilityChange);

        const outputCanvas = this._useWebGL ? this._renderer.canvas : this._outputCanvasElement;

        return outputCanvas.captureStream(parseInt(this._renderFrameRate, 10));
    }

    /**
     * Whether requestVideoFrameCallback can currently drive the loop.
     *
     * @private
     * @returns {boolean}
     */
    _canUseRVFC() {
        return this._rvfcSupported && !document.hidden;
    }

    /**
     * Schedules the next render tick using rVFC when possible, otherwise the timer worker.
     *
     * @private
     * @returns {void}
     */
    _scheduleNext() {
        if (this._stopped) {
            return;
        }

        if (this._canUseRVFC()) {
            this._activeDriver = 'rvfc';
            this._rvfcId = this._inputVideoElement.requestVideoFrameCallback(this._onVideoFrame);
        } else {
            this._activeDriver = 'timer';
            this._maskFrameTimerWorker.postMessage({
                id: SET_TIMEOUT,
                timeMs: this._renderIntervalMs
            });
        }
    }

    /**
     * requestVideoFrameCallback handler.
     *
     * @private
     * @returns {void}
     */
    _onVideoFrame() {
        if (this._stopped) {
            return;
        }
        this._renderTick(performance.now());
        this._scheduleNext();
    }

    /**
     * Timer worker message handler (fallback driver).
     *
     * @private
     * @param {MessageEvent} response - The worker message.
     * @returns {void}
     */
    _onMaskFrameTimer(response) {
        if (this._stopped) {
            return;
        }
        if (response.data.id === TIMEOUT_TICK) {
            this._renderTick(performance.now());
            this._scheduleNext();
        }
    }

    /**
     * Switches to the timer worker when the tab becomes hidden (rVFC stops firing while hidden,
     * which would freeze the outgoing video). The next visible tick switches back to rVFC.
     *
     * @private
     * @returns {void}
     */
    _onVisibilityChange() {
        if (this._stopped) {
            return;
        }
        if (document.hidden && this._activeDriver === 'rvfc') {
            if (typeof this._inputVideoElement.cancelVideoFrameCallback === 'function' && this._rvfcId) {
                this._inputVideoElement.cancelVideoFrameCallback(this._rvfcId);
                this._rvfcId = 0;
            }
            this._scheduleNext();
        }
    }

    /**
     * Runs one render tick: throttled segmentation + compositing.
     *
     * @private
     * @param {number} now - Current high-resolution timestamp (ms).
     * @returns {void}
     */
    _renderTick(now) {
        if (this._tickInProgress) {
            return;
        }

        // rVFC fires at the camera's native fps; throttle compositing to the target render cadence.
        if (this._lastRenderTime && (now - this._lastRenderTime) < this._renderIntervalMs * 0.85) {
            return;
        }
        if (!this._inputVideoElement.videoWidth) {
            return;
        }

        this._tickInProgress = true;

        try {
            this._lastRenderTime = now;

            const needsInference = !this._hasMask || (now - this._lastInferenceTime) >= this._inferenceIntervalMs;

            if (needsInference) {
                const inferenceStart = performance.now();

                this.resizeSource();
                this.runInference();

                this._updateInferenceBudget(performance.now() - inferenceStart);
                this._lastInferenceTime = now;
                this._hasMask = true;
            }

            this.runPostProcessing();
        } catch (error) {
            console.error('Virtual background render error', error);
        } finally {
            this._tickInProgress = false;
        }
    }

    /**
     * Adapts the inference cadence to the measured cost of segmentation on this device, keeping the
     * heavy work to roughly {@code _inferDutyTarget} of wall-clock time. Only how often the mask is
     * recomputed changes - compositing (and therefore the output frame rate) is never reduced - so
     * the video stays smooth and only the mask gets slightly staler under load.
     *
     * @private
     * @param {number} durationMs - Time the last segmentation pass took (ms).
     * @returns {void}
     */
    _updateInferenceBudget(durationMs) {
        this._inferDurationEma = this._inferDurationEma
            ? (this._inferDurationEma * 0.7) + (durationMs * 0.3)
            : durationMs;

        const desiredIntervalMs = this._inferDurationEma / this._inferDutyTarget;

        this._inferenceIntervalMs = Math.min(
            this._maxInferenceIntervalMs,
            Math.max(this._tierInferenceIntervalMs, desiredIntervalMs)
        );
    }

    /**
     * Downscales the current camera frame to the model input resolution and writes the normalized
     * RGB values into the model's input tensor.
     *
     * @returns {void}
     */
    resizeSource() {
        this._segmentationMaskCtx.drawImage(
            this._inputVideoElement,
            0,
            0,
            this._inputVideoElement.videoWidth,
            this._inputVideoElement.videoHeight,
            0,
            0,
            this._options.width,
            this._options.height
        );

        const imageData = this._segmentationMaskCtx.getImageData(
            0,
            0,
            this._options.width,
            this._options.height
        );
        const inputMemoryOffset = this._model._getInputMemoryOffset() / 4;

        for (let i = 0; i < this._segmentationPixelCount; i++) {
            this._model.HEAPF32[inputMemoryOffset + (i * 3)] = imageData.data[i * 4] / 255;
            this._model.HEAPF32[inputMemoryOffset + (i * 3) + 1] = imageData.data[(i * 4) + 1] / 255;
            this._model.HEAPF32[inputMemoryOffset + (i * 3) + 2] = imageData.data[(i * 4) + 2] / 255;
        }
    }

    /**
     * Runs the segmentation model and writes the temporally-smoothed person probability into the
     * mask's alpha channel, then uploads/draws the mask depending on the active compositing path.
     *
     * @returns {void}
     */
    runInference() {
        this._model._runInference();

        const outputMemoryOffset = this._model._getOutputMemoryOffset() / 4;
        const data = this._segmentationMask.data;
        const alpha = this._maskSmoothing;
        const oneMinusAlpha = 1 - alpha;
        const twoChannel = this._segmentationType === 'twoChannel';

        for (let i = 0; i < this._segmentationPixelCount; i++) {
            let person;

            if (twoChannel) {
                // Meet lite/full models output [background, person] logits per pixel; softmax them.
                const background = this._model.HEAPF32[outputMemoryOffset + (i * 2)];
                const personLogit = this._model.HEAPF32[outputMemoryOffset + (i * 2) + 1];
                const shift = Math.max(background, personLogit);
                const backgroundExp = Math.exp(background - shift);
                const personExp = Math.exp(personLogit - shift);

                person = personExp / (backgroundExp + personExp);
            } else {
                // Landscape model outputs a single person-probability channel.
                person = this._model.HEAPF32[outputMemoryOffset + i];
            }

            // EMA smoothing reduces flicker and lets inference run below the render rate.
            const smoothed = (this._smoothedMask[i] * oneMinusAlpha) + (person * alpha);

            this._smoothedMask[i] = smoothed;
            data[(i * 4) + 3] = smoothed * 255;
        }

        if (this._useWebGL) {
            this._renderer.updateMask(this._segmentationMask);
        } else {
            this._segmentationMaskCtx.putImageData(this._segmentationMask, 0, 0);
        }
    }

    /**
     * Composites the final frame using the latest mask and current video frame.
     *
     * @returns {void}
     */
    runPostProcessing() {
        if (this._useWebGL) {
            const vb = this._options.virtualBackground;

            this._renderer.render({
                video: this._inputVideoElement,
                mode: vb.backgroundType === 'image' ? 'image' : 'blur',
                blurValue: vb.blurValue || 8
            });

            return;
        }

        this._runPostProcessing2D();
    }

    /**
     * 2D-canvas + CSS-filter compositing fallback (used when WebGL is unavailable).
     *
     * @private
     * @returns {void}
     */
    _runPostProcessing2D() {
        const ctx = this._outputCanvasCtx;
        const outputW = this._outputCanvasElement.width;
        const outputH = this._outputCanvasElement.height;

        ctx.globalCompositeOperation = 'copy';

        // Smooth out the mask edges.
        ctx.filter = this._options.virtualBackground.backgroundType === 'image' ? 'blur(4px)' : 'blur(8px)';
        ctx.drawImage(
            this._segmentationMaskCanvas,
            0,
            0,
            this._options.width,
            this._options.height,
            0,
            0,
            outputW,
            outputH
        );

        // Draw the foreground video where the mask is set.
        ctx.globalCompositeOperation = 'source-in';
        ctx.filter = 'none';
        ctx.drawImage(this._inputVideoElement, 0, 0, outputW, outputH);

        // Draw the background behind it.
        ctx.globalCompositeOperation = 'destination-over';
        if (this._options.virtualBackground.backgroundType === 'image') {
            if (this._virtualImage && this._virtualImage.complete) {
                ctx.drawImage(this._virtualImage, 0, 0, outputW, outputH);
            }
        } else {
            ctx.filter = `blur(${this._options.virtualBackground.blurValue}px)`;
            ctx.drawImage(this._inputVideoElement, 0, 0, outputW, outputH);
        }
        ctx.filter = 'none';
    }

    /**
     * Stops the capture and render loop and releases resources.
     *
     * @returns {void}
     */
    stopEffect() {
        this._stopped = true;
        this._loopRunning = false;

        document.removeEventListener('visibilitychange', this._onVisibilityChange);

        if (this._rvfcSupported && this._rvfcId
                && typeof this._inputVideoElement.cancelVideoFrameCallback === 'function') {
            this._inputVideoElement.cancelVideoFrameCallback(this._rvfcId);
        }
        this._rvfcId = 0;

        if (this._maskFrameTimerWorker) {
            this._maskFrameTimerWorker.postMessage({ id: CLEAR_TIMEOUT });
            this._maskFrameTimerWorker.terminate();
            this._maskFrameTimerWorker = null;
        }

        if (this._renderer) {
            this._renderer.dispose();
            this._renderer = null;
        }
    }

    /**
     * Legacy hardware-based frame rate heuristic, used as a fallback when the per-device budget is
     * not provided via options.
     *
     * @returns {number}
     */
    getFrameRate() {
        let framerate = 15;

        if (navigator && navigator.hardwareConcurrency) {
            if (navigator.hardwareConcurrency > 12) {
                framerate = 20;
            } else if (navigator.hardwareConcurrency <= 12 && navigator.hardwareConcurrency > 4) {
                framerate = 15;
            } else if (navigator.hardwareConcurrency <= 4) {
                framerate = 10;
            }
        }

        return framerate;
    }
}
